import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Coins, Shield, ShieldAlert } from 'lucide-react';
import type { components } from '@weebee/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { queryKeys } from '@/lib/query-keys';
import { formatDate, formatDateTime, formatNumber, formatRelative } from '@/lib/format';
import {
  getUserDetail,
  getUserGameProfile,
  getUserCollectibles,
  listUserTrips,
  getUserModerationHistory,
} from '@/features/users/api';
import { UserRoleBadge, UserStatusBadge } from '@/features/users/components/status-badge';
import { RoleDialog } from '@/features/users/components/role-dialog';
import { EnforceDialog } from '@/features/users/components/enforce-dialog';
import { CoinsDialog } from '@/features/users/components/coins-dialog';
import { EmptyState } from '@/components/state/empty-state';
import { ErrorState } from '@/components/state/error-state';

type UserDetail = components['schemas']['UserDetail'];

export const Route = createFileRoute('/_authenticated/users/$userId')({
  component: UserDetailPage,
});

type Tab = 'overview' | 'game' | 'trips' | 'moderation';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'game', label: 'Game' },
  { id: 'trips', label: 'Trips' },
  { id: 'moderation', label: 'Moderation' },
];

function UserDetailPage() {
  const { userId } = useParams({ from: '/_authenticated/users/$userId' });
  const [tab, setTab] = useState<Tab>('overview');
  const [dialog, setDialog] = useState<null | 'role' | 'enforce' | 'coins'>(null);

  const detail = useQuery({
    queryKey: queryKeys.users.detail(userId),
    queryFn: () => getUserDetail(userId) as Promise<UserDetail>,
  });

  const user = detail.data;

  return (
    <div className="mx-auto max-w-5xl p-8">
      <Link
        to="/users"
        className="wb-small"
        style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginBottom: 16 }}
      >
        <ArrowLeft size={15} />
        All users
      </Link>

      {detail.isLoading ? (
        <span className="wb-skeleton" style={{ width: 240, height: 28 }} />
      ) : detail.error ? (
        <ErrorState error={detail.error} onRetry={() => detail.refetch()} />
      ) : user ? (
        <>
          <header
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 16,
              flexWrap: 'wrap',
              marginBottom: 20,
            }}
          >
            <HeaderAvatar user={user} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <h1 className="wb-h1">{user.display_name || 'Unnamed'}</h1>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  marginTop: 6,
                }}
              >
                <UserRoleBadge role={user.role} />
                <UserStatusBadge user={user} />
                {user.username ? <span className="wb-tiny">@{user.username}</span> : null}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button size="sm" variant="secondary" onClick={() => setDialog('role')}>
                <Shield />
                Role
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setDialog('coins')}>
                <Coins />
                Coins
              </Button>
              <Button size="sm" variant="danger" onClick={() => setDialog('enforce')}>
                <ShieldAlert />
                Moderate
              </Button>
            </div>
          </header>

          <div className="wb-tabs" style={{ marginBottom: 20 }}>
            {TABS.map((t) => (
              <button
                key={t.id}
                className={cn('wb-tab', tab === t.id && 'active')}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'overview' ? <OverviewTab user={user} /> : null}
          {tab === 'game' ? <GameTab userId={userId} /> : null}
          {tab === 'trips' ? <TripsTab userId={userId} /> : null}
          {tab === 'moderation' ? <ModerationTab userId={userId} /> : null}

          <RoleDialog
            open={dialog === 'role'}
            onClose={() => setDialog(null)}
            userId={userId}
            currentRole={user.role}
          />
          <EnforceDialog
            open={dialog === 'enforce'}
            onClose={() => setDialog(null)}
            userId={userId}
          />
          <CoinsDialog open={dialog === 'coins'} onClose={() => setDialog(null)} userId={userId} />
        </>
      ) : null}
    </div>
  );
}

/* ---------- Overview ---------- */

function OverviewTab({ user }: { user: UserDetail }) {
  const ranks = user.ranks;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <CardContent>
          <p className="wb-eyebrow" style={{ marginBottom: 12 }}>
            Account
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3">
            <Field label="User ID" value={user.id} mono />
            <Field label="Phone" value={user.phone} mono />
            <Field label="Email" value={user.email} />
            <Field label="Provider" value={user.provider} />
            <Field label="Subscription" value={user.subscription_tier} />
            <Field label="Phone verified" value={user.phoneVerified ? 'Yes' : 'No'} />
            <Field
              label="Onboarding"
              value={user.has_completed_onboarding ? 'Complete' : 'Incomplete'}
            />
            <Field label="Joined" value={formatDate(user.created_at)} />
            <Field label="Last seen" value={formatRelative(user.last_seen_at)} />
          </dl>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <RankCard label="Daily" info={ranks?.daily} />
        <RankCard label="Weekly" info={ranks?.weekly} />
        <RankCard label="Monthly" info={ranks?.monthly} />
        <RankCard label="All-time" info={ranks?.['all-time']} />
      </div>
    </div>
  );
}

function RankCard({
  label,
  info,
}: {
  label: string;
  info?: components['schemas']['AdminRankInfo'];
}) {
  return (
    <Card>
      <CardContent>
        <p className="wb-tiny">{label} rank</p>
        <p className="wb-h2 wb-mono">{info ? `#${formatNumber(info.rank)}` : '—'}</p>
        {info ? (
          <p className="wb-tiny">
            {formatNumber(info.points)} pts · of {formatNumber(info.total)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/* ---------- Game ---------- */

function GameTab({ userId }: { userId: string }) {
  const profile = useQuery({
    queryKey: queryKeys.users.gameProfile(userId),
    queryFn: () => getUserGameProfile(userId),
  });
  const collectibles = useQuery({
    queryKey: queryKeys.users.collectibles(userId),
    queryFn: () => getUserCollectibles(userId),
  });

  if (profile.isLoading) return <CardSkeleton />;
  if (profile.error) return <ErrorState error={profile.error} onRetry={() => profile.refetch()} />;
  const p = profile.data;
  if (!p) return <EmptyState title="No game profile" />;

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
      <StatCard label="Lifetime points" value={formatNumber(p.lifetime_points)} />
      <StatCard label="Spendable coins" value={formatNumber(p.lifetime_coins)} />
      <StatCard label="Coins earned (total)" value={formatNumber(p.cumulative_coins)} />
      <StatCard label="Trips completed" value={formatNumber(p.trips_completed)} />
      <StatCard label="Distance" value={`${(p.total_distance_m / 1000).toFixed(1)} km`} />
      <StatCard label="Green distance" value={`${(p.green_distance_m / 1000).toFixed(1)} km`} />
      <StatCard
        label="Collectibles"
        value={collectibles.data ? formatNumber(collectibles.data.total_count) : '…'}
      />
    </div>
  );
}

/* ---------- Trips ---------- */

function TripsTab({ userId }: { userId: string }) {
  const trips = useQuery({
    queryKey: [...queryKeys.users.trips(userId), 1],
    queryFn: () => listUserTrips(userId, 1, 25),
  });

  if (trips.isLoading) return <CardSkeleton />;
  if (trips.error) return <ErrorState error={trips.error} onRetry={() => trips.refetch()} />;
  const rows = trips.data?.trips;
  if (!rows || rows.length === 0) return <EmptyState title="No trips yet" />;

  return (
    <div className="wb-table-wrap">
      <table className="wb-table">
        <thead>
          <tr>
            <th className="wb-th">Route</th>
            <th className="wb-th wb-th--right">Distance</th>
            <th className="wb-th">Mode</th>
            <th className="wb-th">Status</th>
            <th className="wb-th wb-th--right">When</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} className="wb-tr">
              <td className="wb-td">
                <span className="wb-small">
                  {t.start_place_name || 'Unknown'} → {t.end_place_name || 'Unknown'}
                </span>
              </td>
              <td className="wb-td wb-td--right wb-mono">{(t.distance_m / 1000).toFixed(1)} km</td>
              <td className="wb-td wb-td--muted">{t.source_type}</td>
              <td className="wb-td wb-td--muted">{t.status}</td>
              <td className="wb-td wb-td--right wb-small">{formatDate(t.start_time)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Moderation ---------- */

function ModerationTab({ userId }: { userId: string }) {
  const history = useQuery({
    queryKey: queryKeys.moderation.userHistory(userId),
    queryFn: () => getUserModerationHistory(userId),
  });

  if (history.isLoading) return <CardSkeleton />;
  if (history.error) return <ErrorState error={history.error} onRetry={() => history.refetch()} />;
  const enforcements = history.data?.enforcements ?? [];
  const signals = history.data?.signals ?? [];

  if (enforcements.length === 0 && signals.length === 0) {
    return <EmptyState title="Clean record" hint="No enforcement actions or abuse signals." />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <CardContent>
          <p className="wb-eyebrow" style={{ marginBottom: 12 }}>
            Enforcement actions
          </p>
          {enforcements.length === 0 ? (
            <p className="wb-small">None.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {enforcements.map((e) => (
                <div key={e.id} className="wb-divider-row">
                  <div>
                    <span style={{ fontWeight: 600 }}>{e.action_type}</span>
                    <span className="wb-tiny">
                      {' '}
                      · {e.previous_status} → {e.new_status}
                    </span>
                    <div className="wb-small">{e.reason}</div>
                  </div>
                  <span className="wb-tiny">{formatDateTime(e.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <p className="wb-eyebrow" style={{ marginBottom: 12 }}>
            Abuse signals
          </p>
          {signals.length === 0 ? (
            <p className="wb-small">None.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {signals.map((s) => (
                <div key={s.id} className="wb-divider-row">
                  <div>
                    <span style={{ fontWeight: 600 }}>{s.signal_type}</span>
                    <span className="wb-tiny">
                      {' '}
                      · {s.source_feature} · severity {s.severity}
                    </span>
                  </div>
                  <span className="wb-tiny">{formatDateTime(s.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------- Shared bits ---------- */

function Field({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div>
      <dt className="wb-tiny">{label}</dt>
      <dd
        className={cn('wb-small', mono && 'wb-mono')}
        style={{ marginTop: 2, wordBreak: 'break-all' }}
      >
        {value || '—'}
      </dd>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent>
        <p className="wb-tiny">{label}</p>
        <p className="wb-h2 wb-mono">{value}</p>
      </CardContent>
    </Card>
  );
}

function CardSkeleton() {
  return (
    <Card>
      <CardContent>
        <span className="wb-skeleton" style={{ width: '60%', marginBottom: 10 }} />
        <span className="wb-skeleton" style={{ width: '40%' }} />
      </CardContent>
    </Card>
  );
}

function HeaderAvatar({ user }: { user: UserDetail }) {
  const url = user.profile_photo_url || user.display_image_url;
  const initial = (user.display_name || user.username || '?').charAt(0).toUpperCase();
  return url ? (
    <img
      src={url}
      alt=""
      style={{ width: 56, height: 56, borderRadius: 999, objectFit: 'cover', flexShrink: 0 }}
    />
  ) : (
    <div
      aria-hidden
      style={{
        width: 56,
        height: 56,
        borderRadius: 999,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--honey-wash)',
        color: 'var(--honey-700)',
        fontWeight: 700,
        fontSize: 22,
      }}
    >
      {initial}
    </div>
  );
}
